import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setInvestmentAmounts,
  setTimePeriods,
  setIntrestRate,
  setTotalValue,
  resetRdData,
  setTotalAmount,
} from "../redux/calculations/rdSlice";
import {
  resetChartData,
  updatedRdChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import arrowImage from "../assets/svg/arrow.svg";
import { useTranslation } from "react-i18next";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const RdCalc = () => {
  const { textData } = useLocale();
  const { rdCalc } = textData;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { investmentAmmount, intrestRate, timePeriod } = useSelector(
    (state) => state.rdCalculator
  );
  const [isFormModified, setIsFormModified] = useState(false);

  const [showTable, setShowTable] = useState(false);
  const [timeUnit, setTimeUnit] = useState("Years");
  const [validationSchema, setValidationSchema] = useState(null);
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [calcDone, setCalcDone] = useState(false);

  const getValidationSchema = () => {
    const baseSchema = {
      investmentAmmount: Yup.number()
        .required(rdCalc.errorInvestmentRequired)
        .min(500, rdCalc.errorInvestmentRange)
        .max(500000, rdCalc.errorInvestmentRange),

      intrestRate: Yup.number()
        .required(rdCalc.errorInterestRequired)
        .min(1, rdCalc.errorInterestRange)
        .max(15, rdCalc.errorInterestRange),
    };

    if (timeUnit === "Years") {
      baseSchema.timePeriod = Yup.number()
        .min(1, rdCalc.errorTimeYearsRange)
        .max(10, rdCalc.errorTimeYearsRange)
        .required(rdCalc.errorTimePeriodRequired);
    } else if (timeUnit === "Months") {
      baseSchema.timePeriod = Yup.number()
        .min(1, rdCalc.errorTimeMonthsRange)
        .max(9, rdCalc.errorTimeMonthsRange)
        .required(rdCalc.errorTimePeriodRequired);
    }
    return Yup.object(baseSchema);
  };
  useEffect(() => {
    setValidationSchema(getValidationSchema());
  }, [timeUnit]);

  const formik = useFormik({
    initialValues: {
      investmentAmmount: investmentAmmount,
      intrestRate: intrestRate,
      timePeriod: timePeriod,
    },
    validationSchema: validationSchema || getValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_RD");
      }
      // Get input values
      const P = Number(values.investmentAmmount);
      const r = Number(values.intrestRate);
      let n = values.timePeriod;

      if (timeUnit === "Years") {
        n = n * 12;
      }

      if (isNaN(P) || isNaN(r) || isNaN(n) || P <= 0 || r <= 0 || n <= 0) {
        formik.setFieldError(
          "general",
          "Invalid input: Enter valid numbers greater than zero."
        );
        return;
      }

      const i = r / (4 * 100);
      const totalInstallments = n;
      const totalInvested = P * totalInstallments;

      // RD maturity amount calculation
      const maturityAmount =
        P *
        ((Math.pow(1 + i, totalInstallments / 3) - 1) /
          (1 - Math.pow(1 + i, -1 / 3)));
      const interestEarned = maturityAmount - totalInvested;

      // Round the values
      const roundedMaturityValue = Math.round(maturityAmount * 100) / 100;

      // Dispatch actions to update state
      dispatch(setTotalValue(roundedMaturityValue));
      dispatch(
        updatedRdChart({
          investAmount: totalInvested,
          estReturn: Math.round(interestEarned * 100) / 100,
          totalAmount: roundedMaturityValue,
        })
      );

      saveToHistory({
        invstmentAmount: P,
        tabName: "RDCalc",
        totalInvestment: totalInvested,
        yearlyReturn: interestEarned,
        interestRate: r,
        timePeriod: n,
        tenure:
          timeUnit === "Years" ? values.timePeriod : values.timePeriod / 12,
        maturityAmount: roundedMaturityValue,
        calculationType: "RD",
        timeUnit: timeUnit,
      });

      dispatch(setTotalAmount(roundedMaturityValue));

      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetRdData());
    };
  }, [dispatch]);
  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        invstmentAmount: calculationData.invstmentAmount,
        timePeriod: calculationData.timePeriod,
        tabName: calculationData.tabName,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.yearlyReturn,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        timeUnit: calculationData.timeUnit,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.maturityAmount === newHistoryRecord.maturityAmount
        );
      });

      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) {}
  };

  useEffect(() => {
    const convertValue = () => {
      const currentValue = formik.values.timePeriod;
      let newValue;

      if (timeUnit === "Years") {
        // if (currentValue > 45) {
        newValue = 3;
        // } else {
        //   newValue = currentValue;
        // }
      } else if (timeUnit === "Months") {
        // if (currentValue < 12) {
        newValue = 3;
        // } else {
        //   newValue = currentValue;
        // }
      }

      formik.setFieldValue("timePeriod", newValue);
      dispatch(setTimePeriods(newValue));

      formik.validateField("timePeriod");
    };

    convertValue();
  }, [timeUnit, dispatch]);

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);

  const getTimeSliderProps = () => {
    if (timeUnit === "Years") {
      return { min: 1, max: 10, step: 1 };
    } else if (timeUnit === "Months") {
      return { min: 1, max: 9, step: 1 };
    }
  };

  const handleTimePeriodSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTimePeriods(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleIntrestRatetSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIntrestRate(value));
    formik.setFieldValue("intrestRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInvestmentAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setInvestmentAmounts(value));
    formik.setFieldValue("investmentAmmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriods(clamped));
    }
    setIsFormModified(true);
    setCalcDone(false);
  };

  const toggleTimeUnit = () => {
    if (timeUnit === "Years") {
      setTimeUnit("Months");
    } else {
      setTimeUnit("Years");
    }
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };

  const handleInvestmentChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number

    if (rawValue.length > 9) return;

    if (rawValue === "") {
      formik.setFieldValue("investmentAmmount", "");
      dispatch(setInvestmentAmounts(""));
      return;
    }
    if (rawValue.match(/^[.,]+$/)) {
      return;
    }
    formik.setFieldValue("investmentAmmount", numericValue);
    dispatch(setInvestmentAmounts(numericValue));
    const formattedValue = new Intl.NumberFormat(i18n.language).format(
      numericValue
    );
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  useEffect(() => {
    dispatch(setInvestmentAmounts(formik.values.investmentAmmount));
    dispatch(setTimePeriods(formik.values.timePeriod));
    dispatch(setIntrestRate(formik.values.intrestRate));
  }, [formik.values, dispatch]);

  const { min, max, step } = getTimeSliderProps();
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.rdContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.rdContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.rdContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.rdContent.howItWorksDescription}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        A = P × (1 + R/N)<sup>N t</sup>
      </p>

      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.rdContent.rdSymbolsTable[0].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              R
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.rdContent.rdSymbolsTable[1].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              N
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.rdContent.rdSymbolsTable[2].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              t
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.rdContent.rdSymbolsTable[3].description}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.rdContent.exampleCalc}
      </h2>

      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.rdContent.exampleDec}
      </p>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong> {textData.rdContent.calculation[0].label}:</strong>{" "}
          {textData.rdContent.calculation[0].value}
        </li>
        <li>
          <strong>{textData.rdContent.calculation[1].label}:</strong>{" "}
          {textData.rdContent.calculation[1].value}
        </li>
        <li>
          <strong>{textData.rdContent.calculation[2].label}:</strong>{" "}
          {textData.rdContent.calculation[2].value}
        </li>
      </ul>

      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.rdContent.intrestDec}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.rdContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li> {textData.rdContent.benefits[0]}</li>
        <li> {textData.rdContent.benefits[1]}</li>

        <li> {textData.rdContent.benefits[2]}</li>

        <li> {textData.rdContent.benefits[3]}</li>

        <li> {textData.rdContent.benefits[4]}</li>
      </ul>
    </div>
  );

  const timeLabel =
    timeUnit === "Years"
      ? t("yearShort")
      : timeUnit === "Months"
      ? t("month").charAt(0).toUpperCase()
      : t("day").charAt(0).toUpperCase();
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  return (
    <>
      <Helmet>
        <title>Recurring Deposit Returns Calculator - Know RD Interest Rate</title>
        <meta
          name="description"
          content="Recurring Deposit Calculator helps you know recurring deposit interest rates, calculate RD return values, and plan secure RD investments with GrowLifeFund."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/recurring-deposit-calculator"
        />

        <meta
          property="og:title"
          content="Recurring Deposit Returns Calculator - Know RD Interest Rate"
        />
        <meta
          property="og:description"
          content="Recurring Deposit Calculator helps you know recurring deposit interest rates, calculate RD return values, and plan secure RD investments with GrowLifeFund."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/recurring-deposit-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Free Online Recurring Deposit Return Calculator - GrowLifeFund"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.rdCalc.rdRecurringDeposit}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.rdCalc.rdReturns}{" "}
            </span>
          </>
        }
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        description={textData.rdCalc.rdDescription}
        timeUnit={timeUnit}
        descriptionContent={descriptionContent}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.rdCalc.monthlyInvest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={sliderTrackRef}>
                  
                  <input
                    type="range"
                    min={500}
                    max={500000}
                    step={500}
                    value={investmentAmmount}
                    onChange={handleInvestmentAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((investmentAmmount - 500) / (500000 - 500)) * 100
                      }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="investmentAmmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.investmentAmmount)}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.investmentAmmount &&
                      formik.errors.investmentAmmount
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`" sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                  />
                  {formik.touched.investmentAmmount &&
                    formik.errors.investmentAmmount && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.investmentAmmount}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={interestSliderRef}>
                  
                  <input
                    type="range"
                    min={1}
                    max={15}
                    step={0.1}
                    value={parseFloat(intrestRate)}
                    onChange={handleIntrestRatetSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((intrestRate - 1) / (15 - 1)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("12.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.intrestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`" sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                  />
                  {formik.touched.intrestRate && formik.errors.intrestRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.intrestRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <div className="flex sm:items-center gap-1 flex-row">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.timePeriod}
                  </label>
                  <div
                    onClick={toggleTimeUnit}
                    className="ml-0 sm:ml-[10px] cursor-pointer items-center flex gap-[2px] text-primary hover:text-primary-dark text-sm md:text-sm font-medium transition focus:outline-none"
                  >
                    {timeUnit === "Years"
                      ? t("years")
                      : timeUnit === "Months"
                      ? t("months")
                      : t("days")}

                    <img
                      src={arrowImage}
                      alt="Dropdown arrow"
                      className="w-3 h-3 sm:w-3 sm:h-3"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={periodSliderRef}>
                  
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={formik.values.timePeriod}
                    onChange={handleTimePeriodSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik.values.timePeriod - min) / (max - min)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={timeLabel}
                    value={formik.values.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={
                      timeUnit === "Years"
                        ? "5"
                        : timeUnit === "Months"
                        ? "9"
                        : "1825"
                    }
                    borderColor={
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`" sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                  />
                  {formik.touched.timePeriod && formik.errors.timePeriod && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.timePeriod}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default RdCalc;
